/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {createPoint, createPolyline} from "@luciad/ria/shape/ShapeFactory";
import {OutOfBoundsError} from "@luciad/ria/error/OutOfBoundsError";

class Measurement {

  constructor(context) {
    this._shapeReference = context.shapeReference;
    this._geodesy = context.geodesy;
    this._worldToModelTx = context.worldToModelTx;
    this._viewToWorldTx = context.viewToWorldTx;
    this._segments = [];
    this._finish = false;
  }

  _createSegment(p1, p2) {
    const segment = {
      line: createPolyline(this._shapeReference, [p1.modelPoint, p2.modelPoint]),
      distance: 0,
      p1: p1,
      p2: p2
    };
    this._segments.push(segment);
  }

  _updateLastSegment(p2, isFinal) {
    const lastIdx = this._segments.length - 1;
    const segment = this._segments[lastIdx];

    // replace last point by p2
    segment.line.removePoint(1);
    segment.line.insertPoint(1, p2.modelPoint);
    segment.p2 = p2;

    // update distance
    segment.distance = this._geodesy.distance3D(segment.p1.worldPoint, p2.worldPoint);
    segment.isFinal = !!isFinal;
  }

  /**
   * Converts the view position to world and model positions ()
   * @param viewPosition
   * @returns {modelPoint, worldPoint}
   * @private
   */
  _getMeasurePoint(viewPosition) {
    try {
      const viewPoint = createPoint(null, viewPosition);
      const worldPoint = this._viewToWorldTx.transform(viewPoint);
      const modelPoint = this._worldToModelTx.transform(worldPoint);
      return {
        worldPoint: worldPoint,
        modelPoint: modelPoint
      }
    } catch (e) {
      if (!(e instanceof OutOfBoundsError)) {
        console.error("Error occurred: " + e);
      }
    }
  }

  reset() {
    this._segments = [];
  }

  isStarted() {
    return this._segments.length && !this._finish;
  }

  updateLastPosition(viewPosition) {
    const measurePoint = this._getMeasurePoint(viewPosition);
    if (!measurePoint) {
      return false;
    }

    this._updateLastSegment(measurePoint);
    return true;
  }

  start(viewPosition) {
    let measurePoint;
    if (!this.isStarted()) {
      this._finish = false;
      this.reset();
      measurePoint = this._getMeasurePoint(viewPosition);

      if (!measurePoint) {
        return false;
      }
      this._createSegment(measurePoint, measurePoint);
    }
    return true;
  }

  addNewPosition(viewPosition) {

    if (this._finish === true) {
      this.reset();
      return true;
    }

    const measurePoint = this._getMeasurePoint(viewPosition);
    if (!measurePoint) {
      return false;
    }

    if (this._segments.length) {
      this._updateLastSegment(measurePoint, true);
    }
    this._createSegment(measurePoint, measurePoint);
    return true;
  }

  stop() {
    //if already finished, just cleanup.
    if (this._finish) {
      this._finish = false;
      this.reset();
      return true;
    }

    if (this._segments.length > 1 && this._segments[this._segments.length - 1].distance === 0) {
      this._segments.pop();
    }

    this._finish = true;
    return true;
  }

  get segments() {
    return this._segments;
  }
}

export {Measurement};

